// src/features/goal/components/GoalActivityHeatmap.tsx
// Goal Detail 頁的活躍度熱力圖
//
// 設計：
// - 資料來源：Tasks 的 closedAt 日期
// - 顯示範圍：過去 26 週（約半年）
// - 顏色深淺：0=底色，1=淡紫，2-3=中紫，4+=深紫

import React, { useMemo, useRef, useEffect } from 'react';
import * as d3 from 'd3';
import { Task } from '../../../types/task';

interface GoalActivityHeatmapProps {
  tasks: Task[];
}

// 顏色定義（Orlo 紫色調）
const COLOR_EMPTY = '#f3f4f6';   // gray-100
const COLOR_L1 = '#e9d5ff';      // purple-200
const COLOR_L2 = '#c084fc';      // purple-400
const COLOR_L3 = '#9333ea';      // purple-600
const COLOR_L4 = '#6b21a8';      // purple-800

function getColor(count: number): string {
  if (count === 0) return COLOR_EMPTY;
  if (count === 1) return COLOR_L1;
  if (count <= 3) return COLOR_L2;
  if (count <= 5) return COLOR_L3;
  return COLOR_L4;
}

const WEEKS = 26;
const CELL_SIZE = 11;
const CELL_GAP = 2;
const DAY_LABEL_WIDTH = 18;
const MONTH_LABEL_HEIGHT = 14;

const GoalActivityHeatmap: React.FC<GoalActivityHeatmapProps> = ({ tasks }) => {
  const svgRef = useRef<SVGSVGElement>(null);

  // 計算每日活動數量
  const dailyCounts = useMemo(() => {
    const counts = new Map<string, number>();

    tasks.forEach(task => {
      if (task.closedAt) {
        const date = task.closedAt instanceof Date ? task.closedAt : new Date(task.closedAt);
        const key = d3.timeFormat('%Y-%m-%d')(date);
        counts.set(key, (counts.get(key) ?? 0) + 1);
      }
    });

    return counts;
  }, [tasks]);

  // 計算日期範圍（過去 26 週）
  const { startDate, endDate, days } = useMemo(() => {
    const end = new Date();
    end.setHours(0, 0, 0, 0);

    // 回推到最近的週日（d3 慣例），再回推 WEEKS 週
    const endWeekStart = d3.timeSunday.ceil(end);
    const start = d3.timeSunday.offset(endWeekStart, -WEEKS);

    const allDays = d3.timeDays(start, endWeekStart);

    return { startDate: start, endDate: endWeekStart, days: allDays };
  }, []);

  // 繪製熱力圖
  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const totalWidth = DAY_LABEL_WIDTH + WEEKS * (CELL_SIZE + CELL_GAP);
    const totalHeight = MONTH_LABEL_HEIGHT + 7 * (CELL_SIZE + CELL_GAP);

    svg.attr('viewBox', `0 0 ${totalWidth} ${totalHeight}`);

    const g = svg.append('g')
      .attr('transform', `translate(${DAY_LABEL_WIDTH}, ${MONTH_LABEL_HEIGHT})`);

    // 繪製格子
    const formatKey = d3.timeFormat('%Y-%m-%d');

    g.selectAll('rect')
      .data(days)
      .enter()
      .append('rect')
      .attr('x', d => d3.timeSunday.count(startDate, d) * (CELL_SIZE + CELL_GAP))
      .attr('y', d => d.getDay() * (CELL_SIZE + CELL_GAP))
      .attr('width', CELL_SIZE)
      .attr('height', CELL_SIZE)
      .attr('rx', 2)
      .attr('fill', d => getColor(dailyCounts.get(formatKey(d)) ?? 0));

    // 月份標籤
    const months = d3.timeMonths(startDate, endDate);
    const formatMonth = d3.timeFormat('%b');

    svg.append('g')
      .selectAll('text')
      .data(months)
      .enter()
      .append('text')
      .attr('x', d => DAY_LABEL_WIDTH + d3.timeSunday.count(startDate, d) * (CELL_SIZE + CELL_GAP))
      .attr('y', MONTH_LABEL_HEIGHT - 3)
      .attr('font-size', '8px')
      .attr('fill', '#9ca3af')
      .text(d => formatMonth(d));

    // 星期標籤（只顯示 Mon, Wed, Fri）
    const dayLabels = [
      { day: 1, label: 'M' },
      { day: 3, label: 'W' },
      { day: 5, label: 'F' },
    ];

    svg.append('g')
      .selectAll('text')
      .data(dayLabels)
      .enter()
      .append('text')
      .attr('x', DAY_LABEL_WIDTH - 4)
      .attr('y', d => MONTH_LABEL_HEIGHT + d.day * (CELL_SIZE + CELL_GAP) + CELL_SIZE - 1)
      .attr('font-size', '8px')
      .attr('fill', '#9ca3af')
      .attr('text-anchor', 'end')
      .text(d => d.label);

  }, [days, startDate, endDate, dailyCounts]);

  const hasActivity = dailyCounts.size > 0;

  return (
    <div className="mb-4">
      <div className="bg-white rounded-xl px-3 py-3 border border-gray-200">
        <div className="overflow-x-auto flex justify-center">
          <svg
            ref={svgRef}
            className="w-full sm:w-auto sm:h-[105px]"
          />
        </div>

        {/* 圖例 */}
        <div className="flex items-center justify-end gap-0.5 mt-1.5">
          <span className="text-[10px] text-gray-400 mr-1">Less</span>
          {[COLOR_EMPTY, COLOR_L1, COLOR_L2, COLOR_L3, COLOR_L4].map((color) => (
            <div
              key={color}
              className="w-2.5 h-2.5 rounded-sm"
              style={{ backgroundColor: color }}
            />
          ))}
          <span className="text-[10px] text-gray-400 ml-1">More</span>
        </div>

        {/* 空狀態提示 */}
        {!hasActivity && (
          <p className="text-xs text-gray-400 text-center mt-2">
            Start completing tasks to see your activity
          </p>
        )}
      </div>
    </div>
  );
};

export default GoalActivityHeatmap;
